// Outreach API — calls ChannelGate endpoints

/**
 * Get the ChannelGate base URL from environment.
 * This is a separate service from sam-dbam — do NOT use getKernelApiBase().
 */
const getChannelGateBase = () => {
  const url = process.env.REACT_APP_CHANNEL_GATE_URL;
  if (!url) throw new Error("REACT_APP_CHANNEL_GATE_URL is not set — cannot send outreach");
  return url.replace(/\/+$/, '');
};

/**
 * Send unified outreach to a batch of recipients via ChannelGate.
 * Routes each recipient based on target_channel and their preferred_channel.
 *
 * @param {string} deploymentSlug - deployment identifier (deploy_slug)
 * @param {string} targetChannel - "auto", "browser", or "sms"
 * @param {Array<{ recipient_id: string, preferred_channel: string, phone_number?: string, email?: string, name?: string }>} recipients
 * @param {string} [templateId] - email template ID to send. Omit to let
 *   ChannelGate use whatever the org saved under Channels → Bindings → Templates.
 * @param {boolean} [useDefaultCopy] - send the platform's built-in wording
 *   instead of the org's saved template. Only the Send dialog sets this, and
 *   only when the sender picks "SamSurveyor default copy" by hand.
 * @returns {Promise<{ deployment_slug: string, total: number, sent: number, prepared: number, failed: number, results: Array }>}
 */
export async function sendOutreach(deploymentSlug, targetChannel, recipients, templateId, useDefaultCopy) {
  const base = getChannelGateBase();
  const url = `${base}/outreach`;
  const body = {
    deployment_slug: deploymentSlug,
    target_channel: targetChannel || 'auto',
    recipients,
  };
  if (templateId) body.template_id = templateId;
  if (useDefaultCopy) body.use_default_copy = true;
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Outreach failed (${resp.status}): ${text || resp.statusText}`);
  }
  return resp.json();
}

/**
 * Fetch the default invitation copy and the list of variables ChannelGate
 * substitutes when it sends. The template editor reads this so it can never
 * advertise a variable — or a default message — that the sender does not use.
 *
 * @returns {Promise<{subject: string, body_text: string, variables: Array<{token: string, description: string, sample: string}>}>}
 */
export async function getDefaultInvitationTemplate() {
  const base = getChannelGateBase();
  const resp = await fetch(`${base}/outreach/email-template`);
  if (!resp.ok) throw new Error(`getDefaultInvitationTemplate failed (${resp.status})`);
  return resp.json();
}

/**
 * Legacy SMS-only outreach (backward compatibility).
 * @deprecated Use sendOutreach() instead.
 */
export async function sendSmsOutreach(deploymentSlug, recipients) {
  const base = getChannelGateBase();
  const url = `${base}/outreach/sms`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      deployment_slug: deploymentSlug,
      recipients,
    }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`SMS outreach failed (${resp.status}): ${text || resp.statusText}`);
  }
  return resp.json();
}

/**
 * Send a utility email via ChannelGate (invites, notifications, etc.).
 *
 * @param {string} to - recipient email
 * @param {string} subject - email subject
 * @param {string} bodyText - plain text body
 * @param {string} [bodyHtml] - optional HTML body
 */
export async function sendEmail(to, subject, bodyText, bodyHtml) {
  const base = getChannelGateBase();
  const url = `${base}/email/send`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ to, subject, body_text: bodyText, body_html: bodyHtml || '' }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Email send failed (${resp.status}): ${text || resp.statusText}`);
  }
  return resp.json();
}

/**
 * Send a welcome/invite email to a newly created user.
 *
 * @param {Object} opts
 * @param {string} opts.email       - recipient email
 * @param {string} opts.orgName     - organization name
 * @param {string} opts.adminName   - name of the admin who sent the invite
 * @param {string} opts.adminEmail  - email of the inviting admin
 * @param {string} opts.inviteToken - secure invite token
 */
export async function sendInviteEmail({ email, orgName, adminName, adminEmail, inviteToken }) {
  const consoleBaseURL = process.env.REACT_APP_CONSOLE_URL || window.location.origin;
  // Use =3D for the equals sign because the mail-send webhook uses quoted-printable encoding
  const setupURL = `${consoleBaseURL}?invite=3D${encodeURIComponent(inviteToken)}`;
  const orgDisplay = orgName || 'your organization';
  const adminDisplay = adminName || adminEmail || 'your administrator';

  const subject = `${adminDisplay} invited you to ${orgDisplay} on Sam Surveyor`;

  const bodyText = [
    `Hello,`,
    ``,
    `${adminDisplay} (${adminEmail || ''}) has invited you to join ${orgDisplay} on Sam Surveyor.`,
    ``,
    `Set up your account here: ${setupURL}`,
    ``,
    `This link will expire in 7 days.`,
    ``,
    `If you did not expect this invitation, you can safely ignore this email.`,
    ``,
    `— The Sam Surveyor Team`,
  ].join('\n');

  const bodyHtml = `
<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background-color:#f3f4f6;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="padding:40px 20px;">
    <tr><td align="center">
      <table width="480" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:12px;box-shadow:0 2px 12px rgba(0,0,0,0.06);padding:40px;">
        <tr><td style="padding-bottom:24px;">
          <h1 style="margin:0;font-size:22px;color:#111827;">Sam Surveyor</h1>
        </td></tr>
        <tr><td style="padding-bottom:16px;">
          <h2 style="margin:0;font-size:18px;color:#111827;">You've been invited!</h2>
        </td></tr>
        <tr><td style="padding-bottom:24px;color:#4b5563;font-size:15px;line-height:1.6;">
          <strong>${adminDisplay}</strong>${adminEmail ? ` (${adminEmail})` : ''} has invited you to join
          <strong>${orgDisplay}</strong> on Sam Surveyor.
        </td></tr>
        <tr><td style="padding-bottom:24px;" align="center">
          <a href="${setupURL}" style="display:inline-block;padding:14px 32px;background-color:#2563eb;color:#ffffff;text-decoration:none;border-radius:8px;font-size:16px;font-weight:600;">
            Set Up Your Account
          </a>
        </td></tr>
        <tr><td style="color:#9ca3af;font-size:13px;line-height:1.5;">
          This link will expire in 7 days.<br>
          If you did not expect this invitation, you can safely ignore this email.
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`.trim();

  return sendEmail(email, subject, bodyText, bodyHtml);
}
